import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../lib/firebase';
import { collection, getDocs, doc, updateDoc, addDoc, Timestamp, writeBatch } from 'firebase/firestore';
import PlayerEditModal from '../components/PlayerEditModal';

interface Player {
    id: string;
    firstName: string;
    lastName: string;
    grade: number;
    gamerTag?: string;
    discordId?: string;
    discordUsername?: string;
    parentName?: string;
    parentDiscordUsername?: string;
    games: {
        smashSolo?: boolean;
        smashCrews?: boolean;
        smashJVCrews?: boolean;
        madden?: boolean;
        rocketLeague?: boolean;
        marioKart?: boolean;
        fortnite?: boolean;
        broadcasting?: boolean;
    };
    permissionSlipOnFile: boolean;
    status: string;
    season?: string;
}

const SEASONS = ['Fall 2025', 'Spring 2026'];
const CURRENT_SEASON = 'Spring 2026';

const GAME_LABELS: Record<string, { short: string; emoji: string }> = {
    smashSolo: { short: 'SSB', emoji: '🎮' },
    smashCrews: { short: 'Crews', emoji: '👥' },
    smashJVCrews: { short: 'JV Crews', emoji: '🛡️' },
    madden: { short: 'Mad', emoji: '🏈' },
    rocketLeague: { short: 'RL', emoji: '🚗' },
    marioKart: { short: 'MK', emoji: '🏎️' },
    fortnite: { short: 'FN', emoji: '🔫' },
    broadcasting: { short: 'BC', emoji: '📺' },
};

export default function AdminRosterPage() {
    const [players, setPlayers] = useState<Player[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [gameFilter, setGameFilter] = useState<string>('all');
    const [selectedSeason, setSelectedSeason] = useState(CURRENT_SEASON);
    const [saving, setSaving] = useState<string | null>(null);

    // Collapsible rows
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);

    // Selection for bulk operations
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkOperating, setBulkOperating] = useState(false);

    // Load roster
    useEffect(() => {
        loadRoster();
    }, []);

    async function loadRoster() {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'playerRoster'));
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as Player));
            setPlayers(data.sort((a, b) => a.lastName.localeCompare(b.lastName)));
        } catch (err) {
            console.error('Error loading roster:', err);
        } finally {
            setLoading(false);
        }
    }

    // Filter players by season and status
    const filteredPlayers = players.filter(p => {
        const seasonMatch = !p.season || p.season === selectedSeason;
        if (!seasonMatch) return false;

        // Game filter
        if (gameFilter !== 'all') {
            const gameKey = gameFilter as keyof Player['games'];
            if (!p.games?.[gameKey]) {
                return false;
            }
        }

        if (filter === 'active') return p.status === 'active';
        if (filter === 'inactive') return p.status === 'inactive' || p.status === 'ineligible' || p.status === 'withdrawn';
        return true;
    });

    const seasonPlayerCount = filteredPlayers.length;

    // Row expand/collapse
    const toggleRow = (id: string, e: React.MouseEvent) => {
        // Don't toggle if clicking on buttons or checkboxes
        if ((e.target as HTMLElement).closest('button, input, a')) return;

        const newSet = new Set(expandedRows);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setExpandedRows(newSet);
    };

    // Open modal for editing
    const openEditModal = (player: Player) => {
        setEditingPlayer(player);
        setModalOpen(true);
    };

    // Open modal for new player
    const openNewPlayerModal = () => {
        setEditingPlayer(null);
        setModalOpen(true);
    };

    // Save player (create or update)
    const handleSavePlayer = async (playerData: Partial<Player> & { id?: string }) => {
        // Filter out undefined values - Firestore doesn't allow undefined
        const cleanData = Object.fromEntries(
            Object.entries(playerData).filter(([key, value]) => value !== undefined && key !== 'id')
        );

        if (playerData.id) {
            // Update existing
            await updateDoc(doc(db, 'playerRoster', playerData.id), {
                ...cleanData,
                updatedAt: Timestamp.now()
            });
            setPlayers(players.map(p =>
                p.id === playerData.id ? { ...p, ...cleanData } as Player : p
            ));
        } else {
            // Create new
            const newDoc = await addDoc(collection(db, 'playerRoster'), {
                ...cleanData,
                season: selectedSeason,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
            const newPlayer = { id: newDoc.id, ...cleanData, season: selectedSeason } as Player;
            setPlayers([...players, newPlayer].sort((a, b) => a.lastName.localeCompare(b.lastName)));
        }
    };

    // Quick toggle for status/permission
    const toggleField = async (playerId: string, field: 'permissionSlipOnFile' | 'status', currentValue: any) => {
        setSaving(playerId);
        try {
            let newValue: any;
            if (field === 'permissionSlipOnFile') {
                newValue = !currentValue;
            } else if (field === 'status') {
                newValue = currentValue === 'active' ? 'inactive' : 'active';
            }

            await updateDoc(doc(db, 'playerRoster', playerId), {
                [field]: newValue,
                updatedAt: Timestamp.now()
            });

            setPlayers(players.map(p =>
                p.id === playerId ? { ...p, [field]: newValue } : p
            ));
        } catch (err) {
            console.error('Error updating field:', err);
        } finally {
            setSaving(null);
        }
    };

    // Selection handlers
    const toggleSelection = (id: string) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedIds(newSet);
    };

    const selectAll = () => {
        if (selectedIds.size === filteredPlayers.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredPlayers.map(p => p.id)));
        }
    };

    // Bulk operations
    const bulkUpdate = async (updates: Partial<Player>) => {
        if (selectedIds.size === 0) return;
        setBulkOperating(true);

        try {
            const batch = writeBatch(db);
            for (const id of selectedIds) {
                batch.update(doc(db, 'playerRoster', id), {
                    ...updates,
                    updatedAt: Timestamp.now()
                });
            }
            await batch.commit();

            setPlayers(players.map(p =>
                selectedIds.has(p.id) ? { ...p, ...updates } : p
            ));
            setSelectedIds(new Set());
        } catch (err) {
            console.error('Error bulk updating:', err);
            alert('Failed to update selected players');
        } finally {
            setBulkOperating(false);
        }
    };

    const getGameBadges = (games: Player['games']) => {
        return Object.entries(games || {})
            .filter(([, active]) => active)
            .map(([key]) => GAME_LABELS[key] || { short: key, emoji: '🎮' });
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-display font-bold text-white">Player Roster</h1>
                    <p className="text-secondary-400">{seasonPlayerCount} players • {selectedSeason}</p>
                </div>
                <button
                    onClick={openNewPlayerModal}
                    className="btn-primary"
                >
                    ➕ Add Player
                </button>
            </div>

            {/* Season & Filters */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                    <label className="text-secondary-400 text-sm">Season:</label>
                    <select
                        value={selectedSeason}
                        onChange={e => {
                            setSelectedSeason(e.target.value);
                            setSelectedIds(new Set());
                            setExpandedRows(new Set());
                        }}
                        className="input py-1.5 px-3 w-auto text-sm"
                    >
                        {SEASONS.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div className="flex gap-1">
                    {(['all', 'active', 'inactive'] as const).map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f
                                ? 'bg-primary-500 text-white'
                                : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
                                }`}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-2">
                    <label className="text-secondary-400 text-sm">Team:</label>
                    <select
                        value={gameFilter}
                        onChange={e => {
                            setGameFilter(e.target.value);
                            setSelectedIds(new Set());
                            setExpandedRows(new Set());
                        }}
                        className="input py-1.5 px-3 w-auto text-sm"
                    >
                        <option value="all">All Teams</option>
                        {Object.entries(GAME_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label.short}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedIds.size > 0 && (
                <div className="mb-4 p-3 bg-primary-500/10 border border-primary-500/30 rounded-lg flex flex-wrap items-center gap-3">
                    <span className="text-primary-400 font-medium">{selectedIds.size} selected</span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => bulkUpdate({ status: 'active' })}
                            disabled={bulkOperating}
                            className="px-3 py-1 text-xs bg-green-500/20 hover:bg-green-500/30 rounded text-green-400"
                        >
                            Set Active
                        </button>
                        <button
                            onClick={() => bulkUpdate({ status: 'inactive' })}
                            disabled={bulkOperating}
                            className="px-3 py-1 text-xs bg-secondary-700 hover:bg-secondary-600 rounded text-secondary-300"
                        >
                            Set Inactive
                        </button>
                        <button
                            onClick={() => bulkUpdate({ permissionSlipOnFile: true })}
                            disabled={bulkOperating}
                            className="px-3 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 rounded text-blue-400"
                        >
                            ✓ Permission
                        </button>
                        <button
                            onClick={() => bulkUpdate({ permissionSlipOnFile: false })}
                            disabled={bulkOperating}
                            className="px-3 py-1 text-xs bg-secondary-700 hover:bg-secondary-600 rounded text-secondary-300"
                        >
                            ✗ Permission
                        </button>
                    </div>
                    <button
                        onClick={() => setSelectedIds(new Set())}
                        className="ml-auto text-xs text-secondary-400 hover:text-white"
                    >
                        Clear
                    </button>
                </div>
            )}

            {/* Roster List */}
            <div className="space-y-2">
                {/* Header Row */}
                <div className="hidden md:grid md:grid-cols-[40px_1fr_50px_120px_100px_80px_80px_30px] gap-2 px-4 py-2 text-xs text-secondary-400 font-medium uppercase">
                    <div>
                        <input
                            type="checkbox"
                            checked={selectedIds.size === filteredPlayers.length && filteredPlayers.length > 0}
                            onChange={selectAll}
                            className="rounded"
                        />
                    </div>
                    <div>Name</div>
                    <div>Gr</div>
                    <div>Games</div>
                    <div>Discord</div>
                    <div className="text-center">Perm</div>
                    <div className="text-center">Status</div>
                    <div></div>
                </div>

                {filteredPlayers.length === 0 ? (
                    <div className="card py-12 text-center text-secondary-400">
                        No players found for {selectedSeason}
                    </div>
                ) : (
                    filteredPlayers.map(player => {
                        const isExpanded = expandedRows.has(player.id);
                        const badges = getGameBadges(player.games);

                        return (
                            <div
                                key={player.id}
                                className={`card overflow-hidden transition-all ${selectedIds.has(player.id) ? 'ring-2 ring-primary-500' : ''
                                    } ${isExpanded ? 'bg-secondary-700' : ''}`}
                            >
                                {/* Collapsed Row */}
                                <div
                                    className="grid grid-cols-[40px_1fr] md:grid-cols-[40px_1fr_50px_120px_100px_80px_80px_30px] gap-2 items-center cursor-pointer hover:bg-secondary-600/30 transition-colors p-4"
                                    onClick={(e) => toggleRow(player.id, e)}
                                >
                                    <div onClick={e => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(player.id)}
                                            onChange={() => toggleSelection(player.id)}
                                            className="rounded"
                                        />
                                    </div>

                                    {/* Name + Tag */}
                                    <div className="min-w-0">
                                        <div className="font-medium text-white truncate">
                                            {player.firstName} {player.lastName}
                                        </div>
                                        {player.gamerTag && (
                                            <div className="text-xs text-secondary-400 truncate">
                                                {player.gamerTag}
                                            </div>
                                        )}
                                    </div>

                                    {/* Grade */}
                                    <div className="hidden md:block text-secondary-300">
                                        {player.grade}
                                    </div>

                                    {/* Games */}
                                    <div className="hidden md:flex flex-wrap gap-1">
                                        {badges.slice(0, 3).map((badge, i) => (
                                            <span
                                                key={i}
                                                className="text-xs bg-secondary-600 px-1.5 py-0.5 rounded text-secondary-300"
                                                title={badge.short}
                                            >
                                                {badge.emoji}
                                            </span>
                                        ))}
                                        {badges.length > 3 && (
                                            <span className="text-xs text-secondary-500">+{badges.length - 3}</span>
                                        )}
                                    </div>

                                    {/* Discord */}
                                    <div className="hidden md:block">
                                        {player.discordUsername ? (
                                            <span className="text-xs bg-indigo-500/20 px-1.5 py-0.5 rounded text-indigo-300 truncate block max-w-[90px]">
                                                @{player.discordUsername}
                                            </span>
                                        ) : (
                                            <span className="text-secondary-500 text-xs">—</span>
                                        )}
                                    </div>

                                    {/* Permission */}
                                    <div className="hidden md:flex justify-center" onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={() => toggleField(player.id, 'permissionSlipOnFile', player.permissionSlipOnFile)}
                                            disabled={saving === player.id}
                                            className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${player.permissionSlipOnFile
                                                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                                : 'bg-secondary-600 text-secondary-500 hover:bg-secondary-500'
                                                }`}
                                        >
                                            {player.permissionSlipOnFile ? '✓' : '—'}
                                        </button>
                                    </div>

                                    {/* Status */}
                                    <div className="hidden md:flex justify-center" onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={() => toggleField(player.id, 'status', player.status)}
                                            disabled={saving === player.id}
                                            className={`text-xs px-2 py-0.5 rounded transition-colors ${player.status === 'active'
                                                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                                : 'bg-secondary-600 text-secondary-400 hover:bg-secondary-500'
                                                }`}
                                        >
                                            {player.status}
                                        </button>
                                    </div>

                                    {/* Expand indicator */}
                                    <div className="hidden md:flex justify-center">
                                        <span className={`text-secondary-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                            ▸
                                        </span>
                                    </div>
                                </div>

                                {/* Expanded Content */}
                                {isExpanded && (
                                    <div className="border-t border-secondary-600 p-4 bg-secondary-800/50 space-y-4">
                                        {/* Mobile: Show hidden fields */}
                                        <div className="md:hidden grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="text-secondary-400">Grade:</span>
                                                <span className="ml-2 text-white">{player.grade}</span>
                                            </div>
                                            <div>
                                                <span className="text-secondary-400">Status:</span>
                                                <span className={`ml-2 ${player.status === 'active' ? 'text-green-400' : 'text-secondary-300'}`}>
                                                    {player.status}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-secondary-400">Permission:</span>
                                                <span className={`ml-2 ${player.permissionSlipOnFile ? 'text-green-400' : 'text-yellow-400'}`}>
                                                    {player.permissionSlipOnFile ? '✓ On File' : '✗ Needed'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Games */}
                                        <div>
                                            <h4 className="text-xs text-secondary-400 uppercase mb-2">Games</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {badges.length > 0 ? badges.map((badge, i) => (
                                                    <span
                                                        key={i}
                                                        className="text-xs bg-primary-500/20 text-primary-400 px-2 py-1 rounded"
                                                    >
                                                        {badge.emoji} {badge.short}
                                                    </span>
                                                )) : (
                                                    <span className="text-secondary-500 text-sm">No games selected</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Discord & Parent */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <h4 className="text-xs text-secondary-400 uppercase mb-1">Discord</h4>
                                                {player.discordUsername ? (
                                                    <span className="text-indigo-300">@{player.discordUsername}</span>
                                                ) : (
                                                    <span className="text-secondary-500">Not linked</span>
                                                )}
                                            </div>
                                            <div>
                                                <h4 className="text-xs text-secondary-400 uppercase mb-1">Parent/Guardian</h4>
                                                <div className="text-secondary-300">
                                                    {player.parentName || <span className="text-secondary-500">—</span>}
                                                </div>
                                                {player.parentDiscordUsername && (
                                                    <div className="text-xs text-indigo-300">@{player.parentDiscordUsername}</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex flex-wrap gap-2 pt-2 border-t border-secondary-700">
                                            <button
                                                onClick={() => openEditModal(player)}
                                                className="btn-primary text-sm py-1.5"
                                            >
                                                ✏️ Edit Player
                                            </button>
                                            <Link
                                                to={`/app/admin/documents?player=${player.id}`}
                                                className="btn-secondary text-sm py-1.5"
                                            >
                                                📄 View Documents
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Quick Stats */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card p-4 text-center">
                    <div className="text-2xl font-bold text-white">{filteredPlayers.filter(p => p.status === 'active').length}</div>
                    <div className="text-sm text-secondary-400">Active</div>
                </div>
                <div className="card p-4 text-center">
                    <div className="text-2xl font-bold text-white">{filteredPlayers.filter(p => p.status !== 'active').length}</div>
                    <div className="text-sm text-secondary-400">Inactive</div>
                </div>
                <div className="card p-4 text-center">
                    <div className="text-2xl font-bold text-green-400">{filteredPlayers.filter(p => p.permissionSlipOnFile).length}</div>
                    <div className="text-sm text-secondary-400">Permission On File</div>
                </div>
                <div className="card p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-400">{filteredPlayers.filter(p => !p.permissionSlipOnFile).length}</div>
                    <div className="text-sm text-secondary-400">Need Permission</div>
                </div>
            </div>

            {/* Edit Modal */}
            <PlayerEditModal
                player={editingPlayer}
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setEditingPlayer(null);
                }}
                onSave={handleSavePlayer}
            />
        </div>
    );
}
